import DOMPurify from 'dompurify'
import {IntlShape} from 'react-intl'
import {LocalStorageAction} from '../types/common'
import {BuilderOptions} from './constants'

export function isEmptyObject(obj: Record<string, unknown> | undefined | null): boolean {
    if (obj === undefined || obj === null || typeof obj !== 'object') {
        return true
    }
    return Object.keys(obj).length === 0
}

export const formatISODateToMMDDYYYY = (isoDateString: string | undefined) => {
    if (!isoDateString) return ''
    if (!isoDateString.includes('T') && !isoDateString.endsWith('Z')) {
        // If the string does not contain 'T' or 'Z', return it as is
        return isoDateString
    }
    const date = new Date(isoDateString)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()

    return `${month}/${day}/${year}`
}

/**
 * Checks if a given date (ISO or MM/dd/yyyy) is within or before N days from today
 * @param dateStr - ISO 8601 string or 'MM/dd/yyyy' string
 * @param days - Number of days from today to compare against
 * @returns boolean - true if the date is within or before N days from today
 */
export function isDateWithinDays(dateStr: string, days: number): boolean {
    const now = new Date()
    now.setHours(0, 0, 0, 0) // zero out time for accurate date comparison

    const formattedDate = formatISODateToMMDDYYYY(dateStr)
    const [month, day, year] = formattedDate.split('/').map(Number)
    const targetDate = new Date(year, month - 1, day)
    targetDate.setHours(0, 0, 0, 0)

    const maxDate = new Date(now)
    maxDate.setDate(now.getDate() + days)

    return targetDate >= now && targetDate <= maxDate
}

export const isClient = !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
)

const isNonProdDomain = (): boolean => {
    if (!isClient) return false
    const hostname = window.location.hostname
    return (
        hostname.includes('devhec.com') || hostname.startsWith('stg.') || hostname === 'localhost'
    )
}

export const isProduction = process.env.NODE_ENV === 'production'

export const isNonProduction = isNonProdDomain()

/**
 * Unescapes HTML entities in a string.
 * @param escapedHtml - The string containing HTML entities to unescape.
 * @returns The unescaped string.
 */
function unescapeHtml(escapedHtml: string | undefined | null): string {
    if (!escapedHtml) return ''
    // Handle both browser and server environments
    if (typeof document !== 'undefined') {
        // Browser environment
        const textarea = document.createElement('textarea')
        textarea.innerHTML = escapedHtml
        return textarea.textContent || ''
    } else {
        // Server environment - simple string replacement
        return escapedHtml
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&#x2F;/g, '/')
    }
}

/**
 * Sanitizes content from Salesforce Commerce Cloud (SFCC).
 * It unescapes HTML entities and sanitizes the content to allow only specific tags and attributes.
 * @param content - The content string to be sanitized.
 * @returns The sanitized HTML string.
 */
export function sanitizeSfccContent(content?: string): string {
    const unescapedContent = unescapeHtml(content)
    return DOMPurify.sanitize(unescapedContent, {
        ALLOWED_TAGS: [
            'b',
            'i',
            'em',
            'strong',
            'p',
            'br',
            'ul',
            'ol',
            'li',
            'a',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'div'
        ],
        ALLOWED_ATTR: ['href', 'target', 'class', 'style']
    })
}

export const localStorageAction = (
    action: LocalStorageAction['action'],
    key: string,
    value?: string
): void => {
    if (!isClient) return
    switch (action) {
        case 'set':
            saveToLocalStorage(key, value || '')
            break
        case 'get':
            return getFromLocalStorage<string>(key)
            break
        case 'remove':
            localStorage.removeItem(key)
            break
        default:
            console.warn(`Unknown localStorage action`)
    }
}

const saveToLocalStorage = (key: string, value: string): void => {
    if (isClient) {
        try {
            localStorage.setItem(key, JSON.stringify(value))
        } catch (error) {
            console.error(`Error saving to localStorage for key "${key}":`, error)
        }
    }
}
const getFromLocalStorage = <T>(key: string): any => {
    if (isClient) {
        try {
            const value = localStorage.getItem(key)
            return value ? JSON.parse(value) : null
        } catch (error) {
            console.error(`Error retrieving from localStorage for key "${key}":`, error)
            return null
        }
    }
    return null
}

export const isValidBuilderOptions = (options?: BuilderOptions): boolean => {
    return !!(
        options &&
        options.model &&
        typeof options.model === 'string' &&
        options.model.trim() !== ''
    )
}

// Helper to check if a JWT token is expired
export const isJWTExpired = (jwt: string) => {
    if (!jwt) return true
    try {
        const base64Url = jwt.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                })
                .join('')
        )
        const payload = JSON.parse(jsonPayload)
        const now = Math.floor(Date.now() / 1000)
        return payload.exp && payload.exp < now
    } catch (e) {
        return true
    }
}

export const getCookieValue = (name: string): string | null => {
    if (!isClient) return null
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
}

export const formatCurrency = (amount: number, intl: IntlShape, currency: string): string => {
    return intl.formatNumber(amount, {
        style: 'currency',
        currency
    })
}
